import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { caseStudyAPI } from '../api/casestudyAPI';
import CaseStudyModal from '../../../components/CaseStudyModal/CaseStudyModal';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import { filterCasesForExamHost, resolveExamHostCaseSubjects, resolvePrimarySubjectCaseSubjects, caseMatchesSubject } from '../../../config/examHost';
import { edgeVisibleIndices, rowViewWindow } from './caseRowEdges';
import './CaseStudiesPage.css';

/** Horizontal row with category header; scroll arrows overlay the row itself
 * (Netflix-style), fading in on hover and only showing when there's more
 * content in that direction to reveal. */
function CaseStudyRowScroller({ title, showNav, children }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [arrowZoneHeight, setArrowZoneHeight] = useState(null);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  // Size the arrow overlay to the thumbnail image, not the card's full height
  // (image + caption) — the caption's height varies independently of the
  // image's (1 vs 2-line title vs. a continuously width-scaled 16:9 image),
  // so no fixed CSS percentage can stay correct for both at once.
  const updateArrowZoneHeight = () => {
    const el = scrollRef.current;
    const imageWrapper = el?.querySelector('.masonry-card-image-wrapper, .film-card-image-wrap');
    if (imageWrapper) setArrowZoneHeight(imageWrapper.offsetHeight);
  };

  // Marks the last ~2 cards actually scrolled into view at each edge (not
  // the last ~2 cards in the full padded list, which can be off-screen
  // placeholders — see getPaddedCategoryCards) so the CSS-only arrow-reveal
  // rule in CaseStudiesPage.css can key off real visible position instead
  // of static DOM position. The scroller is the cards' offsetParent, and the
  // window excludes its side inset, so a card peeking into the gutter is not
  // counted as visible.
  const updateEdgeVisibleCards = () => {
    const el = scrollRef.current;
    if (!el) return;
    const cards = Array.from(el.querySelectorAll(':scope > .masonry-case-card, :scope > .film-card'));
    if (cards.length === 0) return;

    const { viewLeft, viewRight } = rowViewWindow(el);
    const edges = edgeVisibleIndices(
      cards.map((card) => ({ left: card.offsetLeft, width: card.offsetWidth })),
      viewLeft,
      viewRight
    );

    cards.forEach((card) => card.classList.remove('row-edge-visible--left', 'row-edge-visible--right'));
    edges.left.forEach((i) => cards[i].classList.add('row-edge-visible--left'));
    edges.right.forEach((i) => cards[i].classList.add('row-edge-visible--right'));
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    updateArrowZoneHeight();
    updateEdgeVisibleCards();
    const handleScroll = () => {
      updateScrollState();
      updateEdgeVisibleCards();
    };
    const handleResize = () => {
      updateScrollState();
      updateArrowZoneHeight();
      updateEdgeVisibleCards();
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [React.Children.count(children)]);

  const scrollByDirection = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Move 2 cards at a time for smooth navigation
    const firstCard = el.querySelector('.film-card, .masonry-case-card');
    const cardStep = firstCard ? (firstCard.offsetWidth + 8) : 200;
    const delta = 2 * cardStep;

    el.scrollBy({
      left: direction * delta,
      behavior: prefersReduced ? 'auto' : 'smooth'
    });
  };

  return (
    <div className="case-study-category-row">
      <div className="category-header">
        <h2 className="category-title">{title}</h2>
      </div>
      <div className="category-cards-container">
        <div className="category-cards-scroll" ref={scrollRef}>
          {children}
        </div>
        {showNav && (
          <button
            type="button"
            className={`category-row-arrow category-row-arrow--left ${canScrollLeft ? 'is-visible' : ''}`}
            style={{ height: arrowZoneHeight != null ? `${arrowZoneHeight}px` : '100%' }}
            tabIndex={canScrollLeft ? 0 : -1}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              scrollByDirection(-1);
            }}
            aria-label={`Scroll ${title} left`}
          >
            <svg width="40" height="53" viewBox="0 0 24 40" fill="none" aria-hidden>
              <path
                d="M15 34L5 20L15 6"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        {showNav && (
          <button
            type="button"
            className={`category-row-arrow category-row-arrow--right ${canScrollRight ? 'is-visible' : ''}`}
            style={{ height: arrowZoneHeight != null ? `${arrowZoneHeight}px` : '100%' }}
            tabIndex={canScrollRight ? 0 : -1}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              scrollByDirection(1);
            }}
            aria-label={`Scroll ${title} right`}
          >
            <svg width="40" height="53" viewBox="0 0 24 40" fill="none" aria-hidden>
              <path
                d="M9 34L19 20L9 6"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// Comprehensive, 100% safe, educational domain topics covering all categories (12 cards per section)
const DOMAIN_TOPICS = {
  // Law & Civil
  'Civil Rights': [
    'The Voting Rights Legacy', 'Equal Protection in Education', 'The Freedom of Speech Standard',
    'Title IX in Athletics', 'The Right to Counsel', 'Due Process in Schools',
    'Religious Freedom Protections', 'Fair Housing Standards', 'Civic Engagement Protocols',
    'The Public Assembly Doctrine', 'Access to Public Accommodation', 'Universal Ballot Access'
  ],
  'Corporate Law': [
    'The Duty of Loyalty in Boards', 'Shareholder Voting Rights', 'The Business Judgment Rule',
    'Director Oversight Duties', 'Corporate Transparency Standards', 'Fiduciary Duties in Startups',
    'Proxy Voting Frameworks', 'Corporate Governance Auditing', 'Independent Committee Oversight',
    'Securities Disclosure Accuracy', 'Fair Value Appraisal Rights', 'Audit Committee Guidelines'
  ],
  'Criminal Justice': [
    'The Standard of Reasonable Doubt', 'Chain of Custody for Evidence', 'The Right to Speedy Trial',
    'Eyewitness Identification Standards', 'Search Warrants in Digital Spaces', 'Jury Selection Fairness',
    'Admissibility of Expert Testimony', 'Appellate Review Standards', 'The Presumption of Innocence',
    'Statutory Construction Principles', 'Interrogation Rights and Standards', 'Judicial Discretion in Sentencing'
  ],
  'Intellectual Property': [
    'Fair Use in Digital Learning', 'Patent Protections for Inventions', 'Open Source Licensing Rules',
    'Trademark Confusion Standards', 'Trade Secrets in Engineering', 'Copyright in Educational Works',
    'Biotechnology Patent Eligibility', 'Software Algorithm Copyright', 'Design Patent Functionality',
    'International Patent Cooperation', 'Domain Name Dispute Resolution', 'Database Right Protections'
  ],
  'Employment Law-Human Rights': [
    'Workplace Safety Mandates', 'Equal Pay Standards', 'Whistleblower Protections',
    'Non-Compete Enforceability', 'Disability Accommodations at Work', 'Family Leave Protections',
    'Collective Bargaining Principles', 'Wage and Hour Compliance', 'Privacy in Workplace Monitoring',
    'Independent Contractor Classification', 'Constructive Dismissal Precedents', 'Occupational Health Auditing'
  ],
  'Civil': [
    'The Standard of Preponderance', 'Contractual Formation & Intent', 'Negligence & Duty of Care',
    'Remedies in Specific Performance', 'Property Easement Verification', 'Alternative Dispute Resolution',
    'Civil Procedure Jurisdictional Limits', 'Tort Liability in Public Spaces', 'Promissory Estoppel Doctrine',
    'Equitable Estoppel in Commerce', 'Class Action Certification', 'Declaratory Relief Standards'
  ],
  'Protest Movements': [
    'The Public Forum Doctrine', 'Time, Place, and Manner Restrictions', 'Historical Precedents in Peaceful Assembly',
    'Constitutional Protections for Press', 'Permit Application Standards', 'Injunctions Against Demonstrations',
    'Expressive Conduct Protections', 'Student Expression on Campus', 'Symbolic Speech Jurisprudence',
    'Civil Disobedience and the Rule of Law', 'Public Safety Balancing Tests', 'Assembly in Digital Spaces'
  ],
  'MeToo Movement': [
    'Workplace Harassment Policy Frameworks', 'Institutional Reporting Transparency', 'Non-Disclosure Agreement Boundaries',
    'Statute of Limitations Modernization', 'Internal Investigation Standards', 'Retaliation Protection Directives',
    'Board Oversight in Workplace Culture', 'Human Resource Compliance Auditing', 'Third-Party Fact Finding Rules',
    'Victim Advocacy Legal Protections', 'Corporate Code of Conduct Standards', 'Equitable Settlement Guidelines'
  ],
  'Ethics & Laws': [
    'The Precautionary Principle in Law', 'Informed Consent Standards', 'Bioethical Oversight Committees',
    'Confidentiality in Professional Practice', 'Conflict of Interest Disclosures', 'Professional Codes of Ethics',
    'Research Integrity Protocols', 'Duty to Warn and Public Safety', 'Environmental Stewardship Obligations',
    'Whistleblower Ethical Mandates', 'Autonomous Systems Accountability', 'Ethical Review Board Guidelines'
  ],

  // Medicine & Healthcare
  'Healthcare': [
    'Hospital Resource Allocation', 'Preventative Care Strategies', 'Electronic Health Record Privacy',
    'Telemedicine Quality Standards', 'Public Health Nutrition Guidelines', 'Rural Clinic Access Solutions',
    'Infection Control Protocol', 'Primary Care Delivery Models', 'Patient Satisfaction Metrics',
    'Community Health Outreach', 'Health Equity Benchmarks', 'Discharge Planning Safety'
  ],
  'Neurology': [
    'Mapping Synaptic Plasticity', 'Early Cognitive Memory Markers', 'Neural Pathways in Sleep',
    'Neurogenesis and Exercise', 'Motor Function Recovery Protocols', 'Sensory Perception Pathways',
    'Cerebrovascular Autoregulation', 'Neurotransmitter Transport Dynamics', 'Brain-Computer Interface Basics',
    'Glial Cell Functions in Cortex', 'Reflex Arc Transmission Rates', 'Cognitive Assessment Batteries'
  ],
  'Genetics': [
    'CRISPR Diagnostic Precision', 'Mendelian Inheritance Modeling', 'The Human Genome Reference',
    'Epigenetic Influences in Nutrition', 'Gene Expression Regulators', 'DNA Repair Mechanism Models',
    'Population Genetic Drift', 'Polygenic Risk Score Methodology', 'Mitochondrial Inheritance Patterns',
    'Chromosomal Karyotype Analysis', 'Recombinant DNA Safety Protocols', 'RNA Interference Pathways'
  ],
  'Haematology': [
    'Iron Absorption Dynamics', 'Platelet Clotting Cascades', 'Haemoglobin Oxygen Affinity',
    'Blood Plasma Fractionation', 'Erythrocyte Sedimentation Rate', 'Bone Marrow Cell Differentiation',
    'Anticoagulation Monitoring Protocols', 'Blood Transfusion Safety Standards', 'Hemolytic Assay Diagnostics',
    'Leukocyte Phagocytosis Kinetics', 'Coagulation Factor Assays', 'Anemia Classification Framework'
  ],
  'Pediatrics': [
    'Early Childhood Development Milestones', 'Neonatal Nutrition Standards', 'Pediatric Asthma Management',
    'Childhood Immunity Schedules', 'Growth Curve Diagnostic Tracking', 'Pediatric Dosage Calculation Rules',
    'Congenital Screening Protocols', 'Adolescent Sports Health Guidelines', 'Developmental Milestone Screening',
    'Pediatric Emergency Triage', 'Childhood Vision Screening Standards', 'Neonatal Vital Sign Monitoring'
  ],
  'Oncology': [
    'Immunotherapy Target Discovery', 'Early Biomarker Screening', 'Targeted Radiation Precision',
    'Cellular Senescence Pathways', 'Angiogenesis Inhibition Mechanisms', 'Tumour Microenvironment Modeling',
    'Liquid Biopsy Genetic Assays', 'Dose-Limiting Toxicity Analysis', 'Chemotherapy Delivery Systems',
    'Oncogene Activation Pathways', 'Cancer Stem Cell Diagnostics', 'Radiation Field Optimization'
  ],
  'Emergency Care': [
    'Triage Assessment Protocols', 'Airway Management Algorithms', 'Trauma Resuscitation Pathways',
    'Rapid Response Diagnostic Workflow', 'Cardiac Rhythm Stabilization', 'Burn Surface Area Estimation',
    'Mass Casualty Incident Management', 'Point-of-Care Ultrasound Triage', 'Acute Stroke Timeline Protocols',
    'Hypothermia Management Guidelines', 'Poison Control Triage Standards', 'Sepsis Early Warning Scores'
  ],
  'Surgery': [
    'Minimally Invasive Laparoscopy', 'Aseptic Operating Field Protocol', 'Surgical Margin Assessment',
    'Post-Operative Recovery Pathways', 'Electrosurgical Safety Controls', 'Wound Closure Tensile Strength',
    'Surgical Team Time-Out Verification', 'Tissue Perfusion Diagnostics', 'Endoscopic Navigation Calibration',
    'Suture Material Biocompatibility', 'Anesthesia Monitoring Parameters', 'Surgical Instrument Sterilization'
  ],
  'Neurosurgery': [
    'Stereotactic Frame Calibration', 'Intraoperative Neuromonitoring', 'Cranial Pressure Regulation',
    'Microvascular Anastomosis Techniques', 'Neuronavigation Precision Calibration', 'Cerebrospinal Fluid Dynamics',
    'Cortical Mapping Under Anesthesia', 'Spinal Alignment Metric Standards', 'Deep Brain Stimulation Target Mapping',
    'Endovascular Coil Placement Protocol', 'Skull Base Microdissection', 'Optic Nerve Decompression Guidelines'
  ],
  'Palliative Care': [
    'Symptom Management Protocols', 'Multidisciplinary Care Coordination', 'Holistic Patient Comfort Models',
    'Advance Care Planning Frameworks', 'Communication in Complex Prognosis', 'Caregiver Support Architecture',
    'Pain Assessment Scale Calibration', 'Quality of Life Metrics in Care', 'Home Hospice Care Delivery',
    'Continuity of Care Integration', 'Compassionate Care Standards', 'Interdisciplinary Team Collaboration'
  ],
  'Microbiology': [
    'Bacterial Culture Growth Kinetics', 'Gram Staining Precision Methods', 'Antimicrobial Susceptibility Testing',
    'Viral Plaque Assay Methodology', 'Pasteurization Thermal Death Curves', 'Biofilm Formation Dynamics',
    'Agarose Gel Electrophoresis Verification', 'Spore Resistance in Autoclaving', 'Microbial Fermentation Pathways',
    'Pathogen Identification Flowcharts', 'Cellular Morphology Microscopy', 'Aseptic Inoculation Techniques'
  ],
  'Medical Tools': [
    'Diagnostic Ultrasound Calibration', 'Electrocardiograph Lead Positioning', 'Pulse Oximetry Optical Calibration',
    'Magnetic Resonance Gradient Shimming', 'Spirometer Flow Rate Accuracy', 'Automated External Defibrillator Self-Test',
    'Digital Centrifuge RPM Balancing', 'Spectrophotometer Wavelength Calibration', 'Autoclave Temperature Validation',
    'Digital Thermometer Accuracy Verification', 'Blood Gas Analyzer Sensor Drift', 'Infusion Pump Flow Calibration'
  ],

  // Business & Management Consulting
  'Technology': [
    'Cloud Migration Cost Economics', 'SaaS Retention & Churn Dynamics', 'Mobile Platform Ecosystem Strategy',
    'Cybersecurity Threat Assessment', 'AI Tool Deployment Framework', 'Open API Developer Platforms',
    'Legacy System Modernization', 'Data Governance Compliance', 'Serverless Infrastructure Scaling',
    'DevOps Automation Benchmarks', 'Enterprise Software Procurement', 'Micro-Frontend Architecture'
  ],
  'Management Consulting': [
    'Supply Chain Resiliency Modeling', 'Zero-Based Budgeting Frameworks', 'Market Entry Feasibility Assessment',
    'Organizational Design Restructuring', 'Operational Bottleneck Diagnostics', 'Customer Journey Mapping',
    'Pricing Elasticity Optimization', 'Post-Merger Integration Planning', 'ESG Sustainability Auditing',
    'Omnichannel Retail Strategy', 'Competitive Landscape Matrix', 'Digital Transformation Roadmaps'
  ],
  'Retail': [
    'Predictive Inventory Forecasting', 'Direct-to-Consumer Unit Economics', 'Customer Lifetime Value Analytics',
    'Store Footprint Optimization', 'Supply Chain Lead Time Compression', 'Private Label Expansion Strategy',
    'Warehouse Automation Efficiency', 'Dynamic Clearance Pricing Models', 'Omnichannel Fulfillment Speed',
    'Brand Loyalty Program Economics', 'Category Management Assortment', 'Point-of-Sale Data Integration'
  ],
  'Biotech': [
    'Therapeutic Pipeline Valuation', 'Clinical Phase II Success Probability', 'Biomanufacturing Yield Optimization',
    'Regulatory Approval Timelines', 'Biosimilar Market Entry Economics', 'Tech Transfer to CDMO Facilities',
    'Intellectual Property Licensing Strategy', 'Recombinant Protein Purification', 'Cell Therapy Cold Chain Logistics',
    'Companion Diagnostic Co-Development', 'Biofuel Scalability Economics', 'Agricultural Biotech Crop Yield'
  ],
  'Business Analytics': [
    'Cohort Retention Curve Modeling', 'Multi-Touch Attribution Modeling', 'A/B Testing Statistical Significance',
    'Customer Acquisition Cost Payback', 'Time Series Demand Forecasting', 'Churn Risk Classification Trees',
    'Executive Dashboard KPI Architecture', 'Geospatial Customer Penetration', 'Inventory Stockout Simulation',
    'Funnel Conversion Drop-off Analysis', 'Basket Size Affinity Grouping', 'Pricing Sensitivity Clustering'
  ],
  'Automotives': [
    'Electric Powertrain Efficiency', 'Battery Cell Sourcing Contracts', 'Lean Assembly Line Balancing',
    'Autonomous Safety Redundancy', 'Global Tier-1 Supplier Networks', 'Vehicle Lightweighting Materials',
    'Charging Infrastructure Rollout', 'Warranty Claims Root Cause Analysis', 'Modular Vehicle Platform Strategy',
    'Connected Car Data Architecture', 'Aerodynamic Drag Minimization', 'Fleet Electrification TCO'
  ],
  'Aviation': [
    'Airline Route Profitability Modeling', 'Fleet Fuel Burn Reduction Strategy', 'Hub-and-Spoke vs Point-to-Point',
    'Aircraft Turnaround Time Optimization', 'Sustainable Aviation Fuel Economics', 'Crew Scheduling Dynamic Optimization',
    'Passenger Load Factor Maximization', 'Air Cargo Yield Management', 'Aircraft Maintenance Scheduling',
    'Baggage Handling Automation Flow', 'In-Flight Service Cost Optimization', 'Airport Gate Allocation Efficiency'
  ],
  'Strategy': [
    'Core Competency Diversification', 'Strategic Horizon Planning Matrix', 'First-Mover Advantage Sustainability',
    'Vertical Integration Feasibility', 'Disruptive Innovation Assessment', 'Ecosystem Partner Value Sharing',
    'Defensive Moat Analysis', 'Scenario Planning for Market Shocks', 'Global Expansion Localization',
    'Corporate Portfolio Rationalization', 'Platform Network Effects Scaling', 'Strategic Joint Venture Alignment'
  ],

  // Investment Banking & Finance
  'Investment Banking': [
    'Tech IPO Valuation & Bookbuilding', 'Syndicated Leveraged Loan Facility', 'Municipal Infrastructure Bond Issuance',
    'M&A Accretion/Dilution Analysis', 'High-Yield Debt Refinancing', 'Convertible Bond Pricing Models',
    'Cross-Border Acquisition Financing', 'DCF Terminal Value Sensitivity', 'Comparable Company Multiple Analysis',
    'Precedent Transaction Benchmarking', 'Carve-Out Carried Interest Structuring', 'Sovereign Debt Issuance Strategy'
  ],
  'TMT': [
    'Fiber-to-the-Home Capital Outlay', '5G Spectrum Auction Economics', 'Streaming Content Amortization Rules',
    'Data Center REIT Power Density', 'Semiconductor Fab Capex Cycles', 'Satellite Constellation Unit Economics',
    'Telecom Tower Sale-and-Leaseback', 'Cloud Hosting Margin Expansion', 'Digital Media Ad Load Optimization',
    'Gaming Engine Royalty Structures', 'Edge Computing Deployment ROI', 'Enterprise SaaS Multi-Year Contracts'
  ],
  'Hedge Funds': [
    'Long/Short Equity Factor Neutrality', 'Quantitative Momentum Signals', 'Event-Driven Risk Arbitrage Spreads',
    'Fixed Income Basis Trading', 'Global Macro Sovereign Allocation', 'Multi-Strategy Risk Parity Balancing',
    'Statistical Arbitrage Mean Reversion', 'Distressed Debt Restructuring Recovery', 'Volatility Surface Smirk Trading',
    'Commodity Storage Carry Arbitrage', 'Liquidity Constrained Portfolio Sizing', 'Alpha vs Beta Attribution Modeling'
  ],
  'Cryptocurrency': [
    'Consensus Mechanism Energy Metrics', 'Smart Contract Formal Verification', 'Decentralized Liquidity Pool Depth',
    'Layer-2 Rollup Transaction Throughput', 'Cryptographic Proof of Reserves', 'Blockchain Finality Latency',
    'Automated Market Maker Slippage', 'Cross-Chain Bridge Security Auditing', 'Tokenomics Emission Schedules',
    'Zero-Knowledge Succinct Proofs', 'Decentralized Oracle Reliability', 'Wallet Cold Storage Key Ceremony'
  ],
  'Equities': [
    'Dividend Discount Value Modeling', 'Quality Factor Long-Term Returns', 'Small-Cap Value Premium Analysis',
    'Earnings Quality & Accruals Ratio', 'Free Cash Flow Yield Screening', 'Return on Invested Capital Drivers',
    'Equal-Weight vs Market-Cap Indexing', 'Share Buyback Capital Allocation', 'Economic Moat Longevity Assessment',
    'Defensive Sector Beta Characteristics', 'Corporate Cash Conversion Cycles', 'Intrinsic Value Sensitivity Analysis'
  ],
  'FIG': [
    'Commercial Bank Net Interest Margin', 'Tier 1 Common Equity Capital Ratios', 'Non-Performing Loan Coverage Ratios',
    'Insurance Combined Ratio Economics', 'Deposit Beta in Rising Rate Regimes', 'Asset-Liability Duration Matching',
    'Liquidity Coverage Ratio Stress Testing', 'Wealth Management Fee Assets', 'Payment Processor Interchange Take',
    'Credit Card Loss Provisioning Models', 'Central Bank Reserve Requirements', 'Subordinated Debt Bail-in Features'
  ],

  // Computer Science & Engineering
  'Software Development': [
    'Scalable Microservices Architecture', 'Asynchronous Event Handling Flow', 'Database Index Optimization',
    'Designing Resilient REST APIs', 'Automated Regression Test Suites', 'Distributed Locking with Redis',
    'Continuous Integration Pipelines', 'Memory Management in High-Load Apps', 'WebAssembly Performance Profiling',
    'Idempotent API Request Handling', 'Graceful Degradation Under Load', 'Garbage Collection Tuning Strategies'
  ],
  'Computer Science': [
    'Turing Machine Complexity Classes', 'Graph Traversal Search Optimizations', 'Dynamic Programming Subproblems',
    'B-Tree Indexing in File Systems', 'Sorting Algorithm Worst-Case Bounds', 'Relational Normalization Forms',
    'Dining Philosophers Concurrency', 'Huffman Compression Encoding', 'Finite State Automata Compilers',
    'Distributed Hash Table Routing', 'Red-Black Tree Self-Balancing', 'NP-Completeness Proof Reductions'
  ],
  'Hardware Development': [
    'VLSI Logic Gate Layout Placement', 'Printed Circuit Board Signal Integrity', 'FPGA Hardware Acceleration',
    'Thermal Dissipation in Microchips', 'Clock Jitter Minimization Circuits', 'RISC-V Instruction Set Extensions',
    'Semiconductor Photolithography Yield', 'Power Delivery Network Decoupling', 'Bus Arbitration Protocol Timing',
    'Embedded Flash Memory Durability', 'Analog-to-Digital Converter Sampling', 'Electromagnetic Interference Shielding'
  ],
  'Big Data': [
    'Distributed MapReduce Job Partitioning', 'Columnar Storage Snappy Compression', 'Stream Windowing Event Calculations',
    'Data Warehouse Star Schema Design', 'Query Optimization in Parquet Files', 'Real-Time Telemetry Ingestion',
    'Distributed File System Replication', 'Vector Database Indexing at Scale', 'Data Lineage Graph Tracking',
    'ETL Pipeline Idempotency Standards', 'Distributed Joins Skew Mitigation', 'Multi-Tenant Data Lake Isolation'
  ],
  'Data Platforms': [
    'Distributed Consensus in Raft', 'Write-Ahead Log Durability Patterns', 'Zero-Downtime Schema Evolution',
    'Read Replica Replication Lag Mitigation', 'Time-Series Database Compaction', 'Distributed Transaction 2PC Protocol',
    'Key-Value Store Cache Invalidation', 'Database Connection Pooling Metrics', 'Storage Tiering to Cold Archive',
    'Change Data Capture Event Streaming', 'High-Availability Failover Triggers', 'Multi-Region Data Partitioning'
  ],
  'Cryptography': [
    'Diffie-Hellman Key Exchange Math', 'RSA Public Key Prime Factorization', 'Elliptic Curve Point Multiplication',
    'SHA-256 Avalanche Effect Verification', 'Zero-Knowledge Proof Verification', 'HMAC Message Integrity Verification',
    'Public Key Infrastructure Certificates', 'Post-Quantum Lattice Cryptography', 'One-Way Hash Function Collisions',
    'Symmetric AES Block Cipher Modes', 'Digital Signature Non-Repudiation', 'Side-Channel Timing Attack Defenses'
  ],
  'Robotics': [
    'Forward and Inverse Kinematics', 'LIDAR Simultaneous Localization (SLAM)', 'PID Controller Trajectory Tracking',
    'Computer Vision Object Segmentation', 'Brushless Motor Torque Control', 'End-Effector Precision Gripping',
    'Sensor Fusion Kalman Filter Algorithm', 'Obstacle Avoidance Path Planning', 'Real-Time Embedded Robot Operating',
    'Actuator Thermal Load Protection', 'Robotic Arm Payloading Limits', 'Autonomous Return-to-Base Protocols'
  ],
  'Engineering': [
    'Structural Tensile Strength Testing', 'Fluid Dynamics in Venturi Tubes', 'Thermodynamic Carnot Efficiency',
    'Finite Element Stress Modeling', 'Vibration Resonance Frequency Avoidance', 'Corrosion Prevention in Alloys',
    'Heat Exchanger Log Mean Temp', 'Piping Friction Loss Calculations', 'Gearbox Mechanical Advantage Design',
    'Fatigue Life S-N Curve Prediction', 'Pressure Vessel Safety Margins', 'Hydraulic System Flow Rate Balance'
  ],
  'Renewables': [
    'Photovoltaic Cell Spectral Response', 'Wind Turbine Betz Limit Efficiency', 'Grid Battery Storage Round-Trip',
    'Hydroelectric Pelton Wheel Dynamics', 'Geothermal Rankine Binary Cycle', 'Solar Inverter MPPT Algorithms',
    'Offshore Wind Foundation Anchoring', 'High-Voltage Direct Current (HVDC)', 'Biomass Gasification Temperature',
    'Tidal Stream Kinetic Energy Capture', 'Solar Tracker Dual-Axis Alignment', 'Green Hydrogen Electrolysis Energy'
  ],
  'Energy': [
    'Combined Cycle Gas Turbine Yield', 'Superconducting Power Lines Physics', 'Thermal Storage Phase-Change Materials',
    'Nuclear Fission Pressurized Water', 'Natural Gas Pipeline Compressor Stations', 'Pumped Hydroelectric Energy Storage',
    'Electrical Grid Phase Angle Stability', 'Substation Transformer Cooling', 'Microgrid Islanding Synchronization',
    'Fuel Cell Proton Exchange Membrane', 'Industrial Cogeneration Steam Reuse', 'Power Factor Correction Capacitor Banks'
  ],
  'Manufacturing': [
    'Statistical Process Control Charts', 'Six Sigma DMAIC Quality Framework', 'CNC Milling Toolpath Optimization',
    'Injection Molding Cycle Time Reduction', 'Overall Equipment Effectiveness (OEE)', 'Just-in-Time Kanban Card Systems',
    'Poka-Yoke Mistake Proofing Devices', 'Assembly Line Value Stream Mapping', 'Cleanroom Airborne Particle Control',
    'Sheet Metal Bending K-Factor Limits', 'Ultrasonic Non-Destructive Testing', 'Automated Optical Inspection Yield'
  ],
  'Physics': [
    'Young Double-Slit Light Wave Proof', 'Michelson-Morley Interferometer Path', 'Photoelectric Effect Frequency Threshold',
    'Cavendish Gravitational Constant G', 'Superconductivity Meissner Effect', 'Special Relativity Time Dilation Metric',
    'Blackbody Radiation Planck Curves', 'Doppler Redshift in Astronomy', 'Compton Scattering Photon Momentum',
    'Hall Effect Charge Carrier Density', 'Faraday Electromagnetic Induction', 'Quantum Harmonic Oscillator States'
  ],
  'Mathematics': [
    'Euclidean Algorithm for Prime GCD', 'Euler Characteristic for Polyhedra', 'Fundamental Theorem of Calculus Proof',
    'Fourier Series Harmonic Decomposition', 'Gaussian Normal Distribution Integrals', 'Matrix Eigenvalue Decomposition',
    'Riemann Zeta Function Prime Counting', 'Cauchy-Schwarz Inequality Proofs', 'Combinatorial Pigeonhole Principle',
    'Laplace Transform Differential Systems', 'Bayes Theorem Prior to Posterior', 'Lagrange Multiplier Optimization'
  ],
  'Algebraic Geometry': [
    'Affine Variety Zero Sets', 'Projective Space Homogeneous Polynomials', 'Hilbert Nullstellensatz Foundations',
    'Bézout Intersection Multiplicity', 'Elliptic Curve Group Addition Law', 'Sheaf Theory Local Data Gluing',
    'Riemann-Roch Divisor Arithmetic', 'Zariski Topology Closed Sets', 'Birational Equivalence Surfaces',
    'Mori Minimal Model Program Basics', 'Kähler Manifold Holomorphic Forms', 'Moduli Space of Algebraic Curves'
  ],
  'Computability Theory': [
    'Turing Machine Halting Problem Proof', 'Gödel First Incompleteness Theorem', 'Church-Turing Thesis Equivalences',
    'Rice Theorem on Semantic Properties', 'Recursive vs Recursively Enumerable', 'Post Correspondence Problem Instances',
    'Ackermann Function Non-Primitive Growth', 'Chaitin Constant Incompressibility', 'Oracle Turing Machine Relative Halting',
    'Arithmetical Hierarchy Quantifier Steps', 'Kleene S-m-n Parameter Theorem', 'Universal Turing Machine Encoding'
  ],
  'Evolutionary Biology': [
    'Darwinian Natural Selection Mechanisms', 'Hardy-Weinberg Genetic Equilibrium', 'Allopatric Speciation Geographic Barriers',
    'Endosymbiotic Organelle Evolution', 'Phylogenetic Tree Cladistic Analysis', 'Fossil Stratigraphy Dating Methods',
    'Adaptive Radiation in Island Habitats', 'Homologous Anatomical Structures', 'Kin Selection Hamilton Rule Ratio',
    'Convergent Evolution in Distant Clades', 'Neutral Theory of Molecular Evolution', 'Epigenetic Inheritance in Lineages'
  ],

  // Geography & Earth Sciences
  'Environment': [
    'Carbon Offset Verification Standards', 'Transboundary Aquifer Conservation', 'Renewable Grid Interconnection Studies',
    'Wetland Ecosystem Biodiversity Metrics', 'Coastal Erosion Buffer Zone Planning', 'Industrial Water Discharge Limits',
    'Marine Sanctuary Boundary Enforcement', 'Methane Emission Reduction Audits', 'Reforestation Satellite Monitoring',
    'Soil Organic Carbon Sequestration', 'Microplastic Watershed Filtration', 'Geothermal Surface Heat Protection'
  ],
  'Geography': [
    'Geographic Information Systems (GIS)', 'River Basin Drainage Density Models', 'Plate Tectonics Continental Drift',
    'Köppen Climate Classification Zones', 'Glacial Moraine Geological Mapping', 'Urban Land Use Burgess Concentric',
    'Atmospheric Hadley Cell Circulation', 'Volcanic Ash Plume Dispersion Modeling', 'Desalination Environmental Impact',
    'Topographic Contour Interpolation', 'Monsoon Seasonal Precipitation Cycles', 'Permafrost Active Layer Thaw Tracking'
  ],
  'Geopolitics': [
    'Maritime Exclusive Economic Zones', 'International River Water Treaties', 'Strategic Maritime Chokepoint Transit',
    'Buffer State Neutrality Doctrines', 'Antarctic Treaty Environmental Protocol', 'Cross-Border Mineral Resource Rights',
    'Sovereignty in Outer Space Exploration', 'Landlocked Nation Transit Rights', 'Airspace Sovereignty Under Chicago Pact',
    'Trans-Arctic Shipping Route Governance', 'Critical Infrastructure Interconnections', 'Diplomatic Immunity Treaty Protections'
  ],
  'Urban Planning': [
    'Transit-Oriented Development Corridors', 'Mixed-Use Zoning Density Guidelines', 'Urban Heat Island Mitigation Surfaces',
    'Pedestrian Walkability Network Indices', 'Stormwater Bioswale Urban Integration', 'Affordable Housing Inclusionary Zoning',
    'Urban Canopy Cover Expansion Goals', 'Protected Bicycle Infrastructure Grid', 'Historic Preservation Building Codes',
    'Public Plaza Sun Exposure Requirements', 'Transfer of Development Rights Programs', 'Zero-Emission Bus Rapid Transit'
  ],
  'Urbanisation': [
    'Metropolitan Commuter Inflow Dynamics', 'Suburban Sprawl Boundary Enforcement', 'Informal Settlement Infrastructure',
    'Urban Population Density Gradients', 'Municipal Solid Waste Recycling Sorting', 'Decentralized District Energy Systems',
    'Smart City Sensor Traffic Management', 'Public Transit ridership Elasticity', 'Brownfield Industrial Site Reclamation',
    'Vertical Farming Energy Consumption', 'Urban Food Supply Distribution Hubs', 'Flood Plain Resilient Architecture'
  ],
  'Climate': [
    'Ice Core Atmospheric Proxy Records', 'El Niño Southern Oscillation Cycles', 'Tropospheric Greenhouse Gas Forcing',
    'Ocean Acidification Carbonate Chemistry', 'Albedo Feedback in Arctic Sea Ice', 'Drought Severity Palmer Indices',
    'Sea Level Rise Tidal Gauge Analysis', 'Extreme Weather Return Period Math', 'Stratospheric Ozone Layer Recovery',
    'Carbon Cycle Deep Ocean Sink Capacity', 'Paleoclimate Tree-Ring Chronologies', 'Solar Irradiance Milankovitch Cycles'
  ],
  'Sustainability': [
    'Life Cycle Assessment Carbon Footprint', 'Circular Economy Closed-Loop Models', 'Cradle-to-Cradle Material Certification',
    'Corporate ESG Scope 1-3 Disclosures', 'Sustainable Forest Stewardship Council', 'Rainwater Harvesting Storage Design',
    'Zero-Waste Manufacturing Plant Audits', 'Organic Agriculture Soil Crop Rotation', 'Green Building LEED Gold Standards',
    'Renewable Energy Certificate Tracking', 'Eco-Label Life Cycle Packaging Rules', 'Municipal Composting Facility Metrics'
  ],
  'Migration': [
    'Demographic Push and Pull Factor Models', 'Urban Labor Market Absorption Patterns', 'Census Spatial Settlement Mapping',
    'Transnational Remittance Economic Impact', 'Historical Migration Corridor Analysis', 'Seasonal Agricultural Labor Flows',
    'Language Acquisition Integration Programs', 'Skill Recognition Framework Standards', 'Regional Population Balancing Policies',
    'Urban Demographic Ageing Offsets', 'Diaspora Community Cultural Networks', 'Border Crossing Documentation Logistics'
  ],
  'Exploration': [
    'Deep Sea Hydrothermal Vent Mapping', 'Stratospheric Weather Balloon Telemetry', 'Subterranean Cave System LiDAR Survey',
    'Orbital Space Telescope Placement', 'Polar Ice Sheet Radar Penetration', 'Remote Sensing of Ancient Archaeological Sites',
    'Planetary Rover Autonomous Navigation', 'Oceanic Trench Bathymetric Profiling', 'Volcanic Crater Gas Sampling Drones',
    'Desert Dune Migration Satellite Tracking', 'Aurora Borealis Geomagnetic Monitoring', 'High-Altitude Atmospheric Physics'
  ],
  'Life Sciences': [
    'Photosynthetic Electron Transport Chain', 'Enzyme Substrate Lock-and-Key Kinetics', 'Cellular Membrane Osmotic Gradients',
    'Plant Vascular Xylem-Phloem Transport', 'ATP Synthase Rotary Motor Dynamics', 'Bioluminescence Luciferase Reactions',
    'Signal Transduction G-Protein Cascades', 'Circadian Rhythm Gene Oscillator Clock', 'Symbiotic Nitrogen Fixing in Legumes',
    'Cellular Apoptosis Programmed Pathways', 'Macromolecular Protein Folding Shapes', 'Immune System Antigen Presentation'
  ]
};

const TEASER_IMAGE_POOL = [
  'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=600&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=600&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=600&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1511497584788-87676104235f?w=600&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=600&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=600&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1551076805-e1869033e561?w=600&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1584515979956-d9f6e5d09982?w=600&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=600&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1450133064473-71024230f91b?w=600&auto=format&fit=crop&q=80',
  'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&auto=format&fit=crop&q=80'
];

const getPaddedCategoryCards = (categoryName, realCards) => {
  const TARGET_CARDS = 12;
  if (realCards.length >= TARGET_CARDS) return realCards;

  const topics = DOMAIN_TOPICS[categoryName] || [
    'Comprehensive Precedent Analysis',
    'Cross-Jurisdictional Framework',
    'Evidentiary Burden Protocol',
    'Strategic Risk Assessment',
    'Statutory Interpretation Review',
    'Regulatory Enforcement Procedure',
    'Judicial Review Case Study',
    'Fiduciary Obligation Standard',
    'Contractual Breach Assessment',
    'Administrative Hearing Procedure',
    'Constitutional Dimension Analysis',
    'Arbitration Award Enforcement'
  ];

  const needed = TARGET_CARDS - realCards.length;
  const placeholdersToAdd = [];

  for (let i = 0; i < needed; i++) {
    const title = topics[i % topics.length];
    const image = TEASER_IMAGE_POOL[(i + categoryName.length) % TEASER_IMAGE_POOL.length];
    placeholdersToAdd.push({
      id: `teaser-${categoryName}-${i}`,
      title,
      subject: categoryName,
      image,
      isPlaceholder: true
    });
  }

  return [...realCards, ...placeholdersToAdd];
};

function sortedCaseSubjects(cards) {
  const subjects = new Set();
  cards.forEach((card) => {
    const subject = (card.subject || '').trim();
    if (subject) subjects.add(subject);
  });
  return Array.from(subjects).sort((a, b) => a.localeCompare(b));
}

/** No "All" pill. Prefer the user's subject; otherwise the first subject. */
function defaultCaseSubject(subjects, userSubject) {
  const preferred = resolvePrimarySubjectCaseSubjects(userSubject);
  for (const want of preferred) {
    const match = subjects.find((subject) => caseMatchesSubject({ subject }, want));
    if (match) return match;
  }
  return subjects[0] || null;
}

const CaseStudiesPage = () => {
  const [filmCards, setFilmCards] = useState([]);
  const [caseStudiesLoading, setCaseStudiesLoading] = useState(true);
  const [selectedSubjectFilter, setSelectedSubjectFilter] = useState(null);
  const filmCardsRef = useRef([]);

  // Modal state
  const [selectedCaseStudy, setSelectedCaseStudy] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();

  // Primary-subject personalisation only picks the default bubble — drop a
  // stale selection so the library opens on the user's subject again.
  useEffect(() => {
    setSelectedSubjectFilter(null);
  }, [user?.primary_subject]);

  // Fetch case studies from database (host-scoped so LNAT gets all Law rows, not a global 1000-slice)
  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        // Only flash the skeleton if we have nothing to show yet — soft-refresh
        // keeps the grid visible so Apps→Cases doesn't look grey/loading.
        if (filmCardsRef.current.length === 0) {
          setCaseStudiesLoading(true);
        }
        const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
        const hostSubjects = resolveExamHostCaseSubjects(user?.primary_subject, hostname);
        const params = { limit: 5000 };
        if (hostSubjects.length === 1) {
          params.subject = hostSubjects[0];
        } else if (hostSubjects.length > 1) {
          params.subjects = hostSubjects.join(',');
        }
        const response = await caseStudyAPI.getCaseStudies(params);
        const hostFiltered = filterCasesForExamHost(response, { hostname });

        const transformedCards = hostFiltered.map((caseStudy, index) => {
          const dbImg = caseStudy.images?.medium || caseStudy.images?.large || caseStudy.images?.small || '/logo-yellow.png';
          const dbTitle = caseStudy.title || `Case Study ${index + 1}`;
          const rawTier = (caseStudy.min_plan_tier || 'beginnings').toLowerCase();
          const cleanTier = rawTier.includes('beyond')
            ? 'Beyond'
            : rawTier.includes('foundations')
              ? 'Foundations'
              : 'Beginnings';

          return {
            id: caseStudy.id,
            title: dbTitle,
            subject: caseStudy.subject || 'General',
            industry: caseStudy.industry || 'General',
            description: caseStudy.description || 'Explore this comprehensive case study designed to challenge your analytical thinking and problem-solving skills.',
            is_locked: caseStudy.is_locked || false,
            required_tier: cleanTier,
            images: {
              small: dbImg,
              medium: dbImg,
              large: dbImg
            },
            image: dbImg,
            fullData: caseStudy
          };
        });

        const seenTitles = new Set();
        const uniqueCards = [];

        transformedCards.forEach((card) => {
          const cleanKey = (card.title || '').trim().toLowerCase();
          if (cleanKey && !seenTitles.has(cleanKey)) {
            seenTitles.add(cleanKey);
            uniqueCards.push(card);
          }
        });

        setFilmCards(uniqueCards);
        filmCardsRef.current = uniqueCards;
      } catch (error) {
        console.error('Error fetching case studies:', error);
        setFilmCards([]);
        filmCardsRef.current = [];
      } finally {
        setCaseStudiesLoading(false);
      }
    };

    fetchCaseStudies();
  }, [user?.primary_subject]);

  const subjectFilterOptions = useMemo(() => sortedCaseSubjects(filmCards), [filmCards]);

  const activeSubjectFilter = useMemo(() => {
    if (subjectFilterOptions.length === 0) return null;
    if (selectedSubjectFilter && subjectFilterOptions.includes(selectedSubjectFilter)) {
      return selectedSubjectFilter;
    }
    return defaultCaseSubject(subjectFilterOptions, user?.primary_subject);
  }, [subjectFilterOptions, selectedSubjectFilter, user?.primary_subject]);

  // One industry row per subject bubble. The bubble defaults to the
  // onboarding subject; every other subject stays one click away.
  const organizedCards = useMemo(() => {
    const categories = {};
    const sourceCards = activeSubjectFilter
      ? filmCards.filter((card) => caseMatchesSubject(card, activeSubjectFilter))
      : filmCards;

    sourceCards.forEach((card) => {
      const categoryKey = card.industry || card.subject || 'General';
      if (!categories[categoryKey]) categories[categoryKey] = [];
      categories[categoryKey].push(card);
    });

    // Sort unlocked cards first within each category
    Object.keys(categories).forEach(catName => {
      categories[catName] = [...categories[catName]].sort((a, b) => {
        if (a.is_locked === b.is_locked) return 0;
        return a.is_locked ? 1 : -1;
      });
    });

    return categories;
  }, [filmCards, activeSubjectFilter]);

  // Get ordered categories sorted by card count (largest first)
  const orderedCategories = useMemo(() => {
    return Object.keys(organizedCards)
      .filter(catName => organizedCards[catName] && organizedCards[catName].length > 0)
      .map(catName => ({
        name: catName,
        cards: organizedCards[catName],
      }))
      .sort((a, b) => b.cards.length - a.cards.length);
  }, [organizedCards]);

  // Modal handlers
  const handleCardClick = (caseStudy) => {
    setSelectedCaseStudy(caseStudy);
    setIsModalOpen(true);
  };

  // Netflix-style card hover preview
  const [hoveredCard, setHoveredCard] = useState(null); // { card, rect }
  const [previewVisible, setPreviewVisible] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const leaveTimeoutRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const handleCardMouseEnter = (card, e) => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    const cardEl = e.currentTarget;

    // Re-entering the same card mid fade-out (or while still fully visible):
    // cancel the fade and snap back to visible immediately, no fresh
    // hover-intent delay needed for a card the user never really left.
    let alreadyShowing = false;
    setHoveredCard((prev) => {
      if (prev && prev.card === card) {
        alreadyShowing = true;
        setPreviewVisible(true);
        return {
          card,
          rect: cardEl.getBoundingClientRect(),
          containerRect: cardEl.closest('.category-cards-container').getBoundingClientRect(),
        };
      }
      return prev;
    });
    if (alreadyShowing) return;

    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredCard({
        card,
        rect: cardEl.getBoundingClientRect(),
        containerRect: cardEl.closest('.category-cards-container').getBoundingClientRect(),
      });
    }, 450);
  };

  const handleCardMouseLeave = (card) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    leaveTimeoutRef.current = setTimeout(() => {
      setHoveredCard((prev) => {
        if (!prev || prev.card !== card) return prev;
        setPreviewVisible(false); // start the fade/shrink out
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = setTimeout(() => {
          setHoveredCard((p) => (p && p.card === card ? null : p));
        }, 320); // matches the CSS transition duration
        return prev; // keep it mounted while it fades out
      });
    }, 150);
  };

  useEffect(() => {
    if (!hoveredCard) {
      setPreviewVisible(false);
      return;
    }
    setPreviewVisible(false);
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setPreviewVisible(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [hoveredCard?.card]);

  useEffect(() => {
    if (!hoveredCard) return;
    const close = () => setHoveredCard(null);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [hoveredCard]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedCaseStudy(null);
  };

  const handleOpenFullStudy = (caseStudyId) => {
    navigate(`/case-study/${caseStudyId}`);
  };

  return (
    <div className="case-studies-page netflix-style">
      <SeoMeta
        title="Interactive Case Studies & Decision Labs | ivori"
        description="Learn through immersive case studies. Practice high-stakes decision making across business strategy, legal disputes, and ethical frameworks."
        keywords="case study learning, decision intelligence labs, interactive case studies, ivori case studies"
        url="https://ivoriai.com/edu/cases"
      />

        {/* Main Content */}
        <main className="case-studies-main">
          <div className="case-studies-content netflix-layout">

            {/* Case Studies Sections */}
            <section className="case-studies-netflix" data-tour="cases-browse">
              {/* Pills when more than one subject is in the library. There is
                  no All pill: the library opens on the user's subject, or the
                  first subject when they have none. */}
              {subjectFilterOptions.length > 1 && !caseStudiesLoading && (
                <div className="case-subject-pills-bar" role="tablist" aria-label="Filter cases by subject">
                  {subjectFilterOptions.map((subject) => (
                    <button
                      key={subject}
                      type="button"
                      role="tab"
                      aria-selected={activeSubjectFilter === subject}
                      className={`case-subject-pill${activeSubjectFilter === subject ? ' active' : ''}`}
                      onClick={() => setSelectedSubjectFilter(subject)}
                    >
                      {subject}
                    </button>
                  ))}
                </div>
              )}
              {caseStudiesLoading ? (
                // Loading state
                <div className="case-study-category">
                  <div className="category-header">
                    <h2 className="category-title">Loading Cases...</h2>
                  </div>
                  <div className="cases-grid-5col">
                    {Array.from({ length: 10 }).map((_, index) => (
                      <div
                        key={`loading-${index}`}
                        className="film-card loading"
                      >
                        <div className="film-card-loading" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : orderedCategories.length === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%', padding: '60px 24px' }}>
                  <div style={{
                    maxWidth: '600px',
                    width: '100%',
                    background: 'var(--iv-band)',
                    border: '1px solid var(--iv-band-line)',
                    borderRadius: '16px',
                    padding: '48px 40px',
                    textAlign: 'center',
                    boxShadow: '0 16px 40px color-mix(in srgb, var(--iv-shadow) 60%, transparent)'
                  }}>
                    <div style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background: 'color-mix(in srgb, var(--iv-on-band) 8%, transparent)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px',
                    }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--iv-on-band)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                      </svg>
                    </div>
                    <h2 style={{
                      fontFamily: 'var(--iv-font-head)',
                      fontWeight: 700,
                      fontSize: '24px',
                      color: 'var(--iv-on-band)',
                      margin: '0 0 12px',
                      lineHeight: '1.3'
                    }}>
                      No case studies available
                    </h2>
                    <p style={{
                      fontSize: '15px',
                      lineHeight: '1.6',
                      color: 'var(--iv-muted)',
                      margin: '0 0 28px',
                      maxWidth: '460px',
                      display: 'inline-block'
                    }}>
                      Case studies could not be loaded. Please check your connection and try again.
                    </p>
                    <div>
                      <button
                        onClick={() => window.location.reload()}
                        style={{
                          background: 'var(--iv-surface)',
                          color: 'var(--iv-body)',
                          border: 'none',
                          borderRadius: '999px',
                          padding: '12px 28px',
                          fontSize: '14.5px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}
                      >
                        Try again
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="23 4 23 10 17 10"></polyline>
                          <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Render each category section in 5-column grid layout
                orderedCategories.map(({ name: categoryName, cards: categoryCards }) => {
                  const displayCards = getPaddedCategoryCards(categoryName, categoryCards);
                  return (
                    <div
                      key={categoryName}
                      className="case-study-category"
                      data-category-name={categoryName}
                    >
                      <CaseStudyRowScroller title={categoryName} showNav={displayCards.length > 0}>
                        {displayCards.length > 0 ? (
                          displayCards.map((card) => {
                            if (card.isPlaceholder) {
                              return (
                                <div
                                  key={`${categoryName}-${card.id}`}
                                  className="masonry-case-card teaser-card"
                                >
                                  <div className="masonry-card-image-wrapper teaser-wrapper">
                                    <img
                                      src={card.image}
                                      alt={card.title}
                                      className="masonry-card-image teaser-image"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/logo-yellow.png';
                                      }}
                                    />
                                    <span className="masonry-card-subject-tag">Available Soon</span>
                                  </div>

                                  <div className="masonry-card-body">
                                    <h3 className="masonry-card-title teaser-title">{card.title}</h3>
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <div
                                key={`${categoryName}-${card.id}`}
                                className="masonry-case-card"
                                onClick={() => handleCardClick(card)}
                                onMouseEnter={(e) => handleCardMouseEnter(card, e)}
                                onMouseLeave={() => handleCardMouseLeave(card)}
                              >
                                <div className="masonry-card-image-wrapper">
                                  <img
                                    src={card.image}
                                    alt={card.title}
                                    className="masonry-card-image"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = '/logo-yellow.png';
                                    }}
                                  />
                                  {card.subject ? (
                                    <span className="masonry-card-subject-tag">{card.subject}</span>
                                  ) : null}
                                  {card.is_locked ? (
                                    <span className="masonry-card-lock-badge">
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                      </svg>
                                      {card.required_tier || 'Foundations'}
                                    </span>
                                  ) : null}
                                </div>

                                <div className="masonry-card-body">
                                  <h3 className="masonry-card-title">{card.title}</h3>
                                  {/* <p className="masonry-card-desc">{cleanDesc}</p> */}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="empty-category-placeholder">
                            <p>Coming soon...</p>
                          </div>
                        )}
                      </CaseStudyRowScroller>
                    </div>
                  );
                })
              )}
            </section>
          </div>
        </main>

        {hoveredCard && (() => {
          const { rect, containerRect } = hoveredCard;
          const previewWidth = rect.width * 1.45;
          const previewHeight = previewWidth * (9 / 16);
          const growW = previewWidth - rect.width;
          const growH = previewHeight - rect.width * (9 / 16);
          const rawLeft = rect.left - growW / 2;
          const rawTop = rect.top - growH * 0.7;
          const clampedLeft = Math.max(
            8,
            containerRect.left + 86, // 70px arrow + 16px gap
            Math.min(
              rawLeft,
              window.innerWidth - previewWidth - 8,
              containerRect.right - previewWidth - 86
            )
          );
          // Keep the grown preview above the Chat/Apps/Cases footer (desktop
          // hover only — touch devices never open this preview).
          const footerEl = document.querySelector('.footer-bar');
          const footerH = footerEl
            ? Math.ceil(footerEl.getBoundingClientRect().height)
            : 64;
          const maxTop = Math.max(8, window.innerHeight - previewHeight - footerH - 8);
          const clampedTop = Math.min(Math.max(8, rawTop), maxTop);
          return ReactDOM.createPortal(
            <div
              className={`case-hover-preview${previewVisible ? ' show' : ''}`}
              style={{
                '--preview-left': `${clampedLeft}px`,
                '--preview-top': `${clampedTop}px`,
                '--preview-width': `${previewWidth}px`,
              }}
              onMouseEnter={() => { if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current); }}
              onMouseLeave={() => handleCardMouseLeave(hoveredCard.card)}
            >
              <img src={hoveredCard.card.image} alt={hoveredCard.card.title} className="case-hover-preview-image" />
              <div className="case-hover-preview-gradient" />
              <div className="case-hover-preview-info">
                {hoveredCard.card.subject && <span className="masonry-card-subject-tag">{hoveredCard.card.subject}</span>}
                <h3 className="case-hover-preview-title">{hoveredCard.card.title}</h3>
                <button
                  className="case-hover-preview-more-btn"
                  onClick={() => { handleCardClick(hoveredCard.card); setHoveredCard(null); }}
                >
                  More
                </button>
              </div>
            </div>,
            document.body
          );
        })()}

        {/* Netflix-style Modal */}
      <CaseStudyModal
        caseStudy={selectedCaseStudy}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onOpenFullStudy={handleOpenFullStudy}
      />
    </div>
  );
};

export default CaseStudiesPage;

